import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import {
  describeApiError,
  extractApiError,
  fieldErrorsFromApiError,
} from '../../../../shared/api/errors';
import { useParametrosMutation, useParametrosQuery } from '../hooks';
import { parametroSistemaInputSchema, type ParametroSistemaFormValues } from '../schemas';
import { TIPOS_APONTAMENTO } from '../types';

const DEFAULT_VALUES: ParametroSistemaFormValues = {
  nome: '',
  casasDecimaisApontamento: 2,
  chaveEmailSite: '',
  tipoApontamentoHora: 'INIFIM',
};

export function ParametrosPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [showSecret, setShowSecret] = useState(false);

  const query = useParametrosQuery();
  const mutation = useParametrosMutation();

  const form = useForm<ParametroSistemaFormValues>({
    resolver: zodResolver(parametroSistemaInputSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (query.data) {
      form.reset({
        nome: query.data.nome,
        casasDecimaisApontamento: query.data.casasDecimaisApontamento,
        chaveEmailSite: query.data.chaveEmailSite ?? '',
        tipoApontamentoHora: query.data.tipoApontamentoHora,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data?.id]);

  async function onSubmit(values: ParametroSistemaFormValues): Promise<void> {
    try {
      await mutation.mutateAsync({
        ...values,
        chaveEmailSite: values.chaveEmailSite || null,
      });
      toast.success(t('features.parametros.toasts.updated'));
    } catch (err) {
      const apiError = extractApiError(err);
      if (apiError) {
        for (const fe of fieldErrorsFromApiError(apiError)) {
          if (fe.campo in DEFAULT_VALUES) {
            form.setError(fe.campo as keyof ParametroSistemaFormValues, { message: fe.mensagem });
          }
        }
      }
      toast.error(describeApiError(err));
    }
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title={t('menu.systemParameters')} />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      </>
    );
  }

  if (query.isError) {
    return (
      <>
        <PageHeader title={t('menu.systemParameters')} />
        <ErrorState description={describeApiError(query.error)} onRetry={() => query.refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.systemParameters')}
        breadcrumbs={[{ label: t('menu.systemParameters') }]}
      />
      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <Box component="form" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <TextField
              label={t('features.parametros.fields.nome')}
              fullWidth
              inputProps={{ maxLength: 150 }}
              error={Boolean(form.formState.errors.nome)}
              helperText={form.formState.errors.nome?.message}
              {...form.register('nome')}
            />
            <Controller
              control={form.control}
              name="casasDecimaisApontamento"
              render={({ field, fieldState }) => (
                <TextField
                  type="number"
                  fullWidth
                  label={t('features.parametros.fields.casasDecimais')}
                  helperText={
                    fieldState.error?.message ?? t('features.parametros.fields.casasDecimaisHelper')
                  }
                  error={Boolean(fieldState.error)}
                  inputProps={{ step: 1, min: 0, max: 6 }}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? null : Number(e.target.value))
                  }
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
            <Controller
              control={form.control}
              name="tipoApontamentoHora"
              render={({ field, fieldState }) => (
                <TextField
                  select
                  fullWidth
                  label={t('features.parametros.fields.tipoApontamento')}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? null : (e.target.value as 'INIFIM' | 'TOTAL'),
                    )
                  }
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                >
                  {TIPOS_APONTAMENTO.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <TextField
              label={t('features.parametros.fields.chaveEmailSite')}
              fullWidth
              type={showSecret ? 'text' : 'password'}
              autoComplete="off"
              inputProps={{ maxLength: 200 }}
              helperText={t('features.parametros.fields.chaveEmailSiteHelper')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showSecret
                          ? t('features.parametros.fields.hideSecret')
                          : t('features.parametros.fields.showSecret')
                      }
                      onClick={() => setShowSecret((s) => !s)}
                      edge="end"
                      size="small"
                    >
                      {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...form.register('chaveEmailSite')}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                type="button"
                onClick={() => query.refetch()}
                disabled={mutation.isPending || query.isFetching}
              >
                {t('common.reload')}
              </Button>
              <Button type="submit" variant="contained" disabled={mutation.isPending}>
                {mutation.isPending ? t('common.saving') : t('common.save')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </>
  );
}
